import FuzzySearch from "fuzzy-search";

import {
  LEVEL_TO_NUMBER,
  NUMBER_TO_LEVEL,
} from "@features/overview-v2/components/tile/utils";
import { type Level } from "@features/overview-v2/types";

import { type BreakdownOption, type ListItem } from "./types";

export function buildListItems(
  labelsByLevel: Record<string, Level>,
  options: BreakdownOption[],
  breakdownKeys: string[],
  breakdownValues: string[][],
  search: string,
  level: number,
  breadcrumbs: string[] = [],
  parentKey = "",
  maxAllowedLevel = 3,
) {
  const items: ListItem[] = [];
  const breakdown = breakdownKeys[level];
  const isLeaf = !breakdownKeys[level + 1];

  if (!breakdown) {
    return items;
  }

  const option = options.find((e) => e.key === breakdown);

  if (option) {
    option.values.forEach((value) => {
      const filters = breakdownValues.filter((e) => e[level] === value);
      const key = `${parentKey} / ${breakdown} : ${value}`;

      const itemMaxAllowedLevel =
        LEVEL_TO_NUMBER[
          labelsByLevel[`${breakdown}:${value}`] ??
            labelsByLevel[`${breakdown}:*`] ??
            "campaign"
        ];
      const reconciledMaxAllowedLevel = Math.min(
        maxAllowedLevel,
        itemMaxAllowedLevel,
      );

      const children = buildListItems(
        labelsByLevel,
        options,
        breakdownKeys,
        filters,
        search,
        level + 1,
        [...breadcrumbs, value],
        key,
        reconciledMaxAllowedLevel,
      );

      const searchValue = [
        ...breadcrumbs,
        value === "" ? "(not set)" : value,
      ].join(" ");

      if (
        (!isLeaf && children.length > 0) ||
        search === "" ||
        (search !== "" &&
          isLeaf &&
          FuzzySearch.isMatch(searchValue, search, false))
      ) {
        const maxLvlNumber =
          children.length > 0
            ? Math.max(
                ...children.map(
                  ({ maxAllowedLevel }) => LEVEL_TO_NUMBER[maxAllowedLevel],
                ),
              )
            : reconciledMaxAllowedLevel;

        items.push({
          breakdown,
          label: value,
          breadcrumbs: [...breadcrumbs, value],
          selected: filters.length > 0,
          selectedCount: filters.length,
          level,
          isLeaf: level === breakdownKeys.length - 1,
          key,
          parentKey,
          children,
          maxAllowedLevel: NUMBER_TO_LEVEL[maxLvlNumber],
        });
      }
    });
  }

  return items;
}
